<script lang="ts">
  import LL, { locale } from '$i18n/i18n-svelte';
  import { getHumanTimeDistanceToNowStrict } from '$lib/utils/locale-date';
  import MatchTimeline from '$lib/er/match/match-timeline.svelte';
  import type { PageData } from './$types';
  import { onDestroy } from 'svelte';
  import { trimTrailingZero } from '$lib/utils/number';
  import Pagination from '$components/pagination.svelte';
  import { xs } from '$lib/utils/media-query';
  import { page } from '$app/stores';
  import { getCurrentSeasonId, getSeasonName, isNormal } from '$lib/er/season/season';
  import QueueSummary from './queue-summary.svelte';
  import PartnersSummary from './partners-summary.svelte';
  import CharacterSummary from './character-summary.svelte';
  import { getRankTierByQueue } from '$lib/er/rank-tiers/calculation';
  import { getTranslatedRankTierName } from '$lib/er/rank-tiers';
  import Adsense from '$components/adsense.svelte';
  import { browser } from '$app/environment';
  import Skeleton from '$components/skeleton.svelte';
  import { erStatic } from '$lib/er/static-all';

  export let data: PageData;
  $: player = data.player!;

  $: queue =
    data.seasonId === 'all' && data.queueType === 'all'
      ? player.queues.find((q) => {
          return q.seasonId === getCurrentSeasonId($erStatic?.result.seasons ?? []);
        })
      : player.queues.find((q) => {
          return q.seasonId === data.seasonId;
        });

  $: selectedOrNormalQueue = queue ?? player.queues.find((q) => q.seasonId === 0);
  $: rankTier = getRankTierByQueue(queue);
  $: winRateText = selectedOrNormalQueue
    ? trimTrailingZero(
        (selectedOrNormalQueue.totalWins / selectedOrNormalQueue.totalPlays) * 100,
        1,
      ) + '%'
    : $LL.global.notEnoughSamples();
  $: seasonDescription =
    selectedOrNormalQueue?.seasonId != 0
      ? `${getSeasonName($LL, selectedOrNormalQueue?.seasonId ?? 0)} ${getTranslatedRankTierName(
          $LL,
          rankTier.tier.tierName,
        )} ${rankTier.lp}RP`
      : getSeasonName($LL, selectedOrNormalQueue?.seasonId ?? 0);
  $: pageDescription = $LL.players.description({
    name: player.name,
    level: player.level.toString(),
    seasonDescription,
    winRate: winRateText,
  });

  let intervalId: NodeJS.Timeout;
  $: lastUpdatedAt = getHumanTimeDistanceToNowStrict(player.updatedAt, $locale);
  $: {
    if (browser) {
      clearInterval(intervalId);
      intervalId = setInterval(() => {
        lastUpdatedAt = getHumanTimeDistanceToNowStrict(player.updatedAt, $locale);
      }, 1000);
    }
  }

  onDestroy(() => {
    if (browser) {
      clearInterval(intervalId);
    }
  });
</script>

<svelte:head>
  <title>{$LL.title({ page: $LL.players.title({ name: player.name }) })}</title>
  <meta name="description" content={pageDescription} />
  <meta name="og:description" content={pageDescription} />
</svelte:head>

<section>
  <aside>
    {#await player.seasonStatistics}
      <div class="panel aside-section-skeleton">
        <Skeleton type="text" size={5} />
        <div>
          <Skeleton type="text" size={4} />
          <Skeleton type="text" size={4} />
        </div>
      </div>
    {:then statistics}
      {#if queue && !isNormal(queue.seasonId)}
        <QueueSummary {queue} {statistics} history={player.history[queue.queueType]} />
      {/if}
      <PartnersSummary playerId={player.id} partners={player.partners} />
      {#if Object.values(statistics.characters).length > 0}
        <CharacterSummary characters={statistics.characters} />
      {/if}
    {/await}
  </aside>
  <div class="timeline">
    <MatchTimeline matches={player.matches.result} />
    <Pagination
      current={player.matches.page[0] + 1}
      max={player.matches.page[1]}
      showEntries={{
        perPage: 20,
        total: player.matches.page[2],
      }}
      maxPagesToShow={$xs ? 10 : 6}
      getHref={(pageNumber) => {
        const newUrl = new URL($page.url);
        newUrl.searchParams.set('page', (pageNumber - 1).toString());
        return newUrl.toString();
      }}
    />
  </div>
  <Adsense adSlot="8605863341" horizontal height="300px" margin="var(--space-4) auto auto auto" />
</section>

<style lang="postcss">
  @import '$lib/styles/breakpoints.css';

  section {
    display: grid;
    grid-template-columns: auto;
    gap: var(--space-5);

    @media (--md) {
      grid-template: auto 1fr / 36rem auto;
    }
  }

  aside {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: 0 var(--space-4);
    @media (--sm) {
      padding: 0;
    }
  }

  .timeline {
    display: flex;
    flex-direction: column;

    @media (--md) {
      grid-row: span 2;
    }
    :global(.pagination) {
      margin: 0 auto;
      margin-top: var(--space-6);
    }
  }

  .aside-section-skeleton {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
</style>
